import { Badge } from "@thilke/ui/components/badge";
import { Input } from "@thilke/ui/components/input";
import { Panel } from "@thilke/ui/components/panel";
import { ScrollArea } from "@thilke/ui/components/scroll-area";
import {
  Status,
  StatusIndicator,
  StatusLabel,
} from "@thilke/ui/components/ui/status";
import { cn } from "@thilke/ui/lib/utils";
import type * as React from "react";

export interface CompendiumEntry {
  id: string;
  title: string;
  type?: string;
  description?: string;
  badge?: string;
}

export interface CompendiumViewProps extends React.ComponentProps<"div"> {
  title?: string;
  entries: CompendiumEntry[];
  query?: string;
  onQueryChange?: (value: string) => void;
  emptyLabel?: string;
}

function CompendiumView({
  title = "Compendium",
  entries,
  query,
  onQueryChange,
  emptyLabel = "No entries",
  className,
  ...props
}: CompendiumViewProps) {
  return (
    <Panel
      position="docked"
      size="md"
      className={cn("grid gap-2.5", className)}
      {...props}
    >
      <header className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">{title}</h2>
        <Status variant={entries.length > 0 ? "info" : "default"}>
          <StatusIndicator />
          <StatusLabel>{entries.length} entries</StatusLabel>
        </Status>
      </header>
      <Input
        value={query}
        onChange={(event) => onQueryChange?.(event.currentTarget.value)}
        placeholder="Filter rules, items, actors..."
        aria-label={`Filter ${title}`}
      />
      <ScrollArea className="h-44 rounded-md border border-border bg-background">
        <div className="grid gap-1.5 p-1.5">
          {entries.length > 0 ? (
            entries.map((entry) => (
              <article
                key={entry.id}
                className="grid gap-1 rounded-md border border-border bg-card/90 px-3 py-2 text-sm"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-foreground">
                    {entry.title}
                  </span>
                  <div className="flex items-center gap-1">
                    {entry.type ? (
                      <Status variant="default">
                        <StatusIndicator />
                        <StatusLabel>{entry.type}</StatusLabel>
                      </Status>
                    ) : null}
                    {entry.badge ? (
                      <Badge variant="outline">{entry.badge}</Badge>
                    ) : null}
                  </div>
                </div>
                {entry.description ? (
                  <p className="line-clamp-2 text-xs text-muted-foreground">
                    {entry.description}
                  </p>
                ) : null}
              </article>
            ))
          ) : (
            <div className="p-3 text-center text-xs text-muted-foreground">
              {emptyLabel}
            </div>
          )}
        </div>
      </ScrollArea>
    </Panel>
  );
}

export { CompendiumView };
